import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  styled,
  Link,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { formattedAmountWithoutAed } from "../../../utils/CommonFunction";
import { statusStyleArray } from "../config";
import { ReactComponent as MwebDhiramIcon } from "../../../assets/svg/MwebDhiramIcon.svg";
import { ReactComponent as ProjectActiveIcon } from "../../../assets/svg/ProjectActiveIcon.svg";
import { ReactComponent as PdfIcon } from "../../../assets/svg/SmallPdfIcon.svg";

const StyledCard = styled(Card)(({ theme }) => ({
  margin: "0 !important",
  borderRadius: "0",
  boxShadow: "none",
  borderBottom: "1px solid #D7D8D9",
}));

export const getStatusDisplayText = (text) => {
  if (typeof text !== "string") return text;
  const displayText = text
    .replace(/-/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  return displayText;
};

export default function MobilePaymentCards({ details, selectedTab }) {
  const navigate = useNavigate();

  const handleProjectClick = (projectId) => {
    navigate(`/project/details/${projectId}`);
  };

  if (!details || details.length === 0) {
    return (
      <Typography
        variant="body1"
        sx={{ textAlign: "center", marginTop: 4, color: "#6B7280" }}
      >
        No payments available
      </Typography>
    );
  }

  return (
    <Box sx={{ padding: "0 2vw", marginTop: "1vh" }}>
      {details.map((item) => {
        const metaData = item?.metadata ? JSON.parse(item?.metadata) : null;
        const fileUrl = metaData?.file?.fileUrl;

        return (
          <StyledCard key={item.id}>
            <CardContent sx={{ padding: "16px 0" }}>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {/* Row 1: Project Name */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleProjectClick(item?.projectId)}
                >
                  <ProjectActiveIcon />
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#0D0D0E",
                      flex: 1,
                    }}
                  >
                    {item?.projectName}
                  </Typography>
                </Box>

                {/* Row 2: Payment Group Name */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#949DAA",
                      fontWeight: 500,
                      marginBottom: "4px",
                    }}
                  >
                    Payment Group
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#0D0D0E",
                    }}
                  >
                    {item?.groupName}
                  </Typography>
                </Box>

                {/* Row 3: Payment Group Status & Due Date */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#949DAA",
                        fontWeight: 500,
                        marginBottom: "4px",
                      }}
                    >
                      Status
                    </Typography>
                    {item?.paymentGroupStatus !== "Created" && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          sx={{
                            width: "12px",
                            height: "12px",
                            borderRadius: "50%",
                            backgroundColor:
                              statusStyleArray.find(
                                (style) =>
                                  style.status === item?.paymentGroupStatus
                              )?.backgroundColor || "transparent",
                            border: "2px solid #e5e5e5",
                            marginRight: "6px",
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: "500",
                            color: "#0D0D0E",
                          }}
                        >
                          {getStatusDisplayText(item?.paymentGroupStatus)}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#949DAA",
                        fontWeight: 500,
                        marginBottom: "4px",
                      }}
                    >
                      Due Date
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#0D0D0E",
                      }}
                    >
                      {moment(item?.dueDate).format("MMM DD, YYYY")}
                    </Typography>
                  </Box>
                </Box>

                {/* Row 4: Release Date & Payout Status */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#949DAA",
                        fontWeight: 500,
                        marginBottom: "4px",
                      }}
                    >
                      Release Date
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#0D0D0E",
                      }}
                    >
                      {item?.releaseDate
                        ? moment(item?.releaseDate).format("MMM DD, YYYY")
                        : "-"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#949DAA",
                        fontWeight: 500,
                        marginBottom: "4px",
                      }}
                    >
                      Payout Status
                    </Typography>
                    {item?.status !== "CREATED" && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          sx={{
                            width: "12px",
                            height: "12px",
                            borderRadius: "50%",
                            backgroundColor:
                              statusStyleArray.find(
                                (style) => style.status === item?.status
                              )?.backgroundColor || "transparent",
                            border: "2px solid #e5e5e5",
                            marginRight: "6px",
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: "500",
                            color: "#0D0D0E",
                          }}
                        >
                          {getStatusDisplayText(item?.status)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Row 5: Amount & Action Column */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#949DAA",
                        fontWeight: 500,
                        marginBottom: "4px",
                      }}
                    >
                      Amount
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "#0D0D0E",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <MwebDhiramIcon />
                      {formattedAmountWithoutAed(item?.amount)}
                    </Typography>
                  </Box>

                  {/* Action Column - Only for selectedTab === 2 */}
                  {selectedTab === 2 && (
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "#949DAA",
                          fontWeight: 500,
                          marginBottom: "4px",
                        }}
                      >
                        Action
                      </Typography>
                      {fileUrl && (
                        <Link
                          href={fileUrl}
                          target="_blank"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            textDecoration: "none",
                          }}
                        >
                          <PdfIcon />
                        </Link>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </StyledCard>
        );
      })}
    </Box>
  );
}
